// Neo-brutalist theme configuration for Delphina
// WCAG AA compliant contrast ratios ensured
// Brand-aligned with Rowan Hale's guidelines

const brand = {
    positive: '#14b8a6', // teal - growth, positive financial data
    authority: '#0891b2', // financial blue - trust, expertise
    warning: '#f59e0b', // warning amber - alerts, caution
    negative: '#f43f5e', // rose - losses, risks
    orange: '#ea580c',
    purple: '#8b5cf6', // violet
    indigo: '#6366f1' // true indigo
}



export const theme = {
    // Background colors
    background: {
        primary: '#FFFFFF', // pure white - primary background
        secondary: '#FAFAFA', // very light grey - secondary backgrounds
        tertiary: '#F5F5F5', // light grey - tertiary backgrounds
        card: '#FFFFFF', // pure white for cards
        overlay: 'rgba(26, 26, 26, 0.8)', // semi-transparent black
        assets: brand.authority,
        income: brand.positive,
        expenses: brand.negative,
        investments: brand.orange,
        cashflow: brand.indigo,
        accessibleAssets: brand.purple
    },

    // Text colors
    text: {
        primary: '#1a1a1a', // black - main text
        secondary: '#374151', // dark grey - secondary text
        tertiary: '#6B7280', // medium grey - tertiary text
        inverse: '#FFFFFF', // white - text on dark backgrounds
        placeholder: '#9CA3AF', // light grey - placeholders
        assets: brand.authority,
        income: brand.positive,
        expenses: brand.negative,
        cashflow: brand.indigo,
        investments: brand.orange,
        accessibleAssets: brand.purple
    },

    // Brand colors (primary palette)
    brand: {
        primary: '#1a1a1a', // black - primary brand color
        secondary: '#FFFFFF', // white - secondary brand color
        positive: brand.positive, // teal - growth, positive financial data
        authority: brand.authority, // financial blue - trust, expertise
        warning: brand.warning, // warning amber - alerts, caution
        negative: brand.negative, // rose - losses, risks
        assets: brand.authority,
        income: brand.positive,
        cashflow: brand.indigo,
        expenses: brand.negative,
        investments: brand.orange,
        accessibleAssets: brand.purple
    },

    // Accent colors (mapped to brand colors)
    accent: {
        positive: brand.positive, // teal - primary actions, positive cash flow
        positiveHover: '#0d9488', // darker teal for hover states
        positiveLight: '#F0FDFA', // very light teal for backgrounds
        negative: brand.negative, // rose - deficits, warnings
        negativeHover: '#E11D48', // darker rose for hover states
        negativeLight: '#FFF1F2', // very light rose for backgrounds
        authority: brand.authority, // financial blue - trust elements
        authorityHover: '#0E7490', // darker blue for hover states
        authorityLight: '#F0F9FF', // very light blue for backgrounds
        warning: brand.warning, // amber - caution elements
        warningHover: '#D97706', // darker amber for hover states
        warningLight: '#FFFBEB', // very light amber for backgrounds
        authority: brand.authority,
        assets: brand.authority,
        income: brand.positive,
        expenses: brand.negative,
        cashflow: brand.indigo,
        investments: brand.orange,
        accessibleAssets: brand.purple
    },

    // Neutral palette for charts and data visualization
    chart: {
        neutral: {
            100: '#F9FAFB',
            200: '#F3F4F6',
            300: '#E5E7EB',
            400: '#D1D5DB',
            500: '#9CA3AF',
            600: '#6B7280',
            700: '#4B5563',
            800: '#374151',
            900: '#1F2937',
        },
        positive: brand.positive, // brand teal for surplus
        negative: brand.negative, // brand rose for shortfall
        authority: brand.authority, // brand blue for authority data
        warning: brand.warning, // brand amber for warnings
    },

    // Border colors (neo-brutalist approach)
    border: {
        primary: '#1a1a1a', // black - 2px minimum borders
        secondary: '#374151', // dark grey - secondary borders
        focus: brand.positive, // teal - focused elements
        error: brand.negative, // rose - error states
        warning: brand.warning, // amber - warning states
    },

    // State colors (brand-aligned)
    state: {
        default: '#6B7280', // medium grey
        hover: '#374151', // darker grey for hover
        focus: brand.positive, // teal for focus
        active: brand.authority, // financial blue for active
        disabled: '#D1D5DB', // light grey for disabled
        loading: '#374151', // dark grey for loading states
    },

    // Shadow colors (brutalist shadows)
    shadow: {
        sm: 'rgba(26, 26, 26, 0.05)', // subtle brutalist shadow
        md: 'rgba(26, 26, 26, 0.1)', // medium brutalist shadow
        lg: 'rgba(26, 26, 26, 0.15)', // large brutalist shadow
        xl: 'rgba(26, 26, 26, 0.25)', // extra large brutalist shadow
    },

    // Semantic color mappings (brand-aligned)
    semantic: {
        success: brand.positive, // maps to brand teal
        warning: brand.warning, // maps to brand amber
        error: brand.negative, // maps to brand rose
        info: brand.authority, // maps to brand financial blue
        primary: '#1a1a1a', // maps to brand black
        secondary: '#374151', // maps to dark grey
    },
};

// Export theme tokens for easy access in components
export const colors = {
    bg: theme.background,
    text: theme.text,
    accent: theme.accent,
    brand: theme.brand,
    chart: theme.chart,
    border: theme.border,
    state: theme.state,
    cashflow: brand.indigo,
    shadow: theme.shadow,
    semantic: theme.semantic,
};

// Helper function to get color value with fallback
export const getColor = (path, fallback = '#000000') => {
    const keys = path.split('.');
    let value = colors;

    for (const key of keys) {
        value = value[key];
        if (value === undefined) return fallback;
    }

    return value;
};

// Export default theme
export default theme;
